import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fiscalApi, type UpdateReportedValuePayload } from './api';

export const fiscalKeys = {
  all: ['fiscal'] as const,
  month: (cnpjId: string | null, month: string) =>
    ['fiscal', 'month', cnpjId, month] as const,
  detail: (id: string) => ['fiscal', 'detail', id] as const,
};

export function useFiscalMonth(cnpjId: string | null, month: string) {
  return useQuery({
    queryKey: fiscalKeys.month(cnpjId, month),
    queryFn: () => fiscalApi.list({ month, cnpjId: cnpjId ?? undefined }),
    enabled: !!cnpjId && !!month,
  });
}

export function useFiscalRecord(id: string | null) {
  return useQuery({
    queryKey: fiscalKeys.detail(id ?? 'noop'),
    queryFn: () => fiscalApi.detail(id!),
    enabled: !!id,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      const s = data.record.status;
      // Polling enquanto há trabalho assíncrono pendente (OCR / emissão NFS-e).
      const pending = data.reports.some((r) =>
        r.ocrStatus === 'PENDING' || r.ocrStatus === 'PROCESSING',
      );
      if (pending) return 3000;
      if (s === 'MATCHED' || s === 'NFSE_EMITTED') return 5000;
      return false;
    },
  });
}

export function useFiscalMutations(id: string | null) {
  const qc = useQueryClient();
  const invalidate = (): void => {
    if (id) qc.invalidateQueries({ queryKey: fiscalKeys.detail(id) });
    qc.invalidateQueries({ queryKey: ['fiscal', 'month'] });
  };

  const uploadReport = useMutation({
    mutationFn: (file: File) => fiscalApi.uploadReport(id!, { file }),
    onSuccess: invalidate,
  });

  const updateReportedValue = useMutation({
    mutationFn: (payload: UpdateReportedValuePayload) =>
      fiscalApi.updateReportedValue(id!, payload),
    onSuccess: invalidate,
  });

  const releaseEmission = useMutation({
    mutationFn: (reason: string) => fiscalApi.releaseEmission(id!, reason),
    onSuccess: invalidate,
  });

  const markPaid = useMutation({
    mutationFn: (paidAt?: string) => fiscalApi.markPaid(id!, paidAt),
    onSuccess: invalidate,
  });

  const retryNfse = useMutation({
    mutationFn: () => fiscalApi.retryNfse(id!),
    onSuccess: invalidate,
  });

  return { uploadReport, updateReportedValue, releaseEmission, markPaid, retryNfse };
}
